<script setup>
import Buttons from '@/components/ui/Buttons.vue';
import { useBlockSafe } from '@/composables/useBlockSafe';
import RedSpot from '@/components/RedSpot.vue';
const props = defineProps({
  data: {
    type: Object,
    default: () => ({}),
  },
});
const {
  id,
  backgroundColor,
  spacingTop,
  spacingBottom,
  title,
  hasTitle,
  description,
  hasDescription,
  image,
  hasImage,
  items,
  hasItems,
  parameters,
  hasParameters,
  hasButtons,
  buttons,
  redSpot,
  hasRedSpot,
  hasAnyContent,
} = useBlockSafe(props.data);
</script>
<template>
  <section
    v-if="hasAnyContent"
    :id="id"
    class="section"
    :class="[
      backgroundColor ? `bg-${backgroundColor}` : '',
      spacingTop ? `section--top-${spacingTop}` : 'section--top-xl',
      spacingBottom ? `section--bottom-${spacingBottom}` : 'section--bottom-xl',
    ]"
    data-aos="fade-up"
    data-aos-easing="ease-out-cubic"
  >
    <RedSpot
      v-if="hasRedSpot"
      :position-vertical="redSpot.vertical"
      :position-horizontal="redSpot.horizontal"
    />
    <div class="container">
      <div class="loyalty-discounts">
        <h2
          v-if="hasTitle"
          class="mb-0 loyalty-discounts__title display text-center"
          :class="[backgroundColor === 'black' ? 'white' : 'black']"
          data-aos="fade-up"
          data-aos-easing="ease-out-cubic"
          v-html="title"
        ></h2>
        <div
          v-if="hasDescription"
          class="loyalty-discounts__text body-1-regular text-center"
          :class="[backgroundColor === 'black' ? 'white' : 'black']"
          data-aos="fade-up"
          data-aos-easing="ease-out-cubic"
          v-html="description"
        ></div>
        <NuxtImg
          v-if="hasImage"
          :src="image?.src"
          :alt="image?.title"
          :height="image?.height"
          :width="image?.width"
          class="loyalty-discounts__image"
          data-aos="zoom-in"
          data-aos-easing="ease-out-quad"
          loading="lazy"
        />
        <div
          v-if="hasParameters || hasItems || hasButtons"
          class="loyalty-discounts__bottom"
        >
          <div
            v-if="hasParameters"
            :class="[
              'loyalty-discounts__parameters',
              `loyalty-discounts__parameters--${backgroundColor}`,
            ]"
          >
            <template
              v-for="(parameter, parameterIndex) in parameters"
              :key="parameter.title"
            >
              <div
                class="loyalty-discounts__parameter"
                data-aos="fade-up"
                :data-aos-delay="
                  parameterIndex >= 1 ? 100 + 100 * parameterIndex : 100
                "
              >
                <p
                  v-if="parameter.title"
                  class="loyalty-discounts__parameter-title h2"
                  :class="[backgroundColor === 'black' ? 'white' : 'black']"
                  v-html="parameter.title"
                ></p>
                <div
                  v-if="parameter.description"
                  class="loyalty-discounts__parameter-description body-3-medium"
                  v-html="parameter.description"
                ></div>
              </div>
            </template>
          </div>
          <ul v-if="hasItems" class="m-0 p-0 loyalty-discounts__items">
            <li
              v-for="(item, itemIndex) in items"
              :key="itemIndex"
              class="m-0 loyalty-discounts__item"
              data-aos="fade-up"
              :data-aos-delay="itemIndex >= 1 ? 100 + 100 * itemIndex : 100"
            >
              <CIcon
                name="state"
                width="32"
                height="32"
                class="loyalty-discounts__item-icon"
              />
              <span
                class="loyalty-discounts__item-title accent"
                :class="[backgroundColor === 'black' ? 'white' : 'black']"
                v-html="item"
              ></span>
            </li>
          </ul>
          <Buttons
            v-if="hasButtons"
            class="loyalty-discounts__buttons"
            :buttons="buttons"
            :background-color="backgroundColor"
            data-aos="fade-up"
            data-aos-easing="ease-out-cubic"
            :buttons-prefix-class="'js-button-loyalty-discounts'"
          />
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped lang="scss">
.loyalty-discounts {
  $parent: &;
  display: flex;
  flex-direction: column;
  gap: 56px;
  @include media-breakpoint-up(sm) {
    gap: 80px;
  }
  @include media-breakpoint-up(xl) {
    @include responsive-spacing(gap, 80);
  }
  &__title {
    margin: 0 auto;
    text-align: center;
    @include media-breakpoint-up(md) {
      width: 66%;
    }
  }
  &__text {
    &:deep(p:last-child) {
      margin-bottom: 0;
    }
    @include media-breakpoint-up(md) {
      max-width: 50%;
      margin: 0 auto;
    }
  }
  &__image {
    height: auto;
    max-width: 550px;
    margin: 0 auto;
    width: 100%;
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(max-width, 550);
    }
  }
  &__bottom {
    display: flex;
    flex-direction: column;
    gap: 32px;
    @include media-breakpoint-up(sm) {
      gap: 48px;
    }
    @include media-breakpoint-up(md) {
      gap: 56px;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 56);
    }
  }
  &__parameters {
    border-radius: 8px;
    display: grid;
    &--white {
      background-color: $gray;
    }
    &--black {
      background-color: rgba($surface-container-opacity, 0.5);
    }
    &--gray {
      background-color: $white;
    }
    @include media-breakpoint-up(md) {
      grid-template-columns: repeat(3, 1fr);
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(border-radius, 8);
    }
  }
  &__parameter {
    border-top: 1px solid $on-surface-variant-2;
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 24px 24px 25px;
    text-align: center;

    @include media-breakpoint-up(sm) {
      gap: 24px;
      padding: 32px 12% 36px;
    }
    @include media-breakpoint-up(md) {
      border-top: unset;
      border-left: 1px solid $on-surface-variant-2;
      padding: 34px 40px 36px;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 24);
      @include responsive-spacing(border-left-width, 1);
      @include responsive-spacing(padding-top, 34);
      @include responsive-spacing(padding-bottom, 36);
      @include responsive-spacing(padding-left, 40);
      @include responsive-spacing(padding-right, 40);
    }
    &:first-child {
      border-top: unset;
      @include media-breakpoint-up(md) {
        border-left: unset;
      }
    }
    &-title {
      margin: 0;
    }
    &-description {
      color: $on-surface-variant;
      @include media-breakpoint-up(sm) {
        padding: 0 24px;
      }
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(padding-left, 24);
        @include responsive-spacing(padding-right, 24);
      }
    }
  }
  &__items {
    align-items: center;
    display: flex;
    flex-direction: column;
    gap: 56px;
    list-style: none;
    @include media-breakpoint-up(sm) {
      gap: 80px;
      margin: 0 auto;
      max-width: 50%;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 80);
    }
  }
  &__item {
    align-items: center;
    display: flex;
    flex-direction: column;
    gap: 16px;
    text-align: center;
    @include media-breakpoint-up(sm) {
      gap: 24px;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 24);
    }
    &-icon {
      height: 32px;
      width: 32px;
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(height, 32);
        @include responsive-spacing(width, 32);
      }
    }
  }
  &__buttons {
    justify-content: center;
  }
}
</style>
